import React, { ReactNode } from 'react'
import { motion, useReducedMotion } from 'framer-motion'
import styled from 'styled-components'

export const SCROLL_REVEAL_STAGGER_S = 0.12

const viewport = { once: true, amount: 0.12, margin: '0px 0px -48px 0px' as const }

const ease = [0.22, 1, 0.36, 1] as const

export const scrollRevealContainerVariants = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: SCROLL_REVEAL_STAGGER_S,
      delayChildren: 0.06,
    },
  },
}

export const scrollRevealItemVariants = {
  hidden: { opacity: 0, y: 28 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, ease },
  },
}

const Root = styled(motion.div)`
  width: 100%;
`

const StaggerRoot = styled(motion.div)`
  width: 100%;
`

const ItemRoot = styled(motion.div)`
  width: 100%;
`

type ScrollRevealProps = {
  children: ReactNode
  className?: string
}

function ScrollReveal({ children, className }: ScrollRevealProps) {
  const prefersReducedMotion = useReducedMotion()

  if (prefersReducedMotion) {
    return <div className={className}>{children}</div>
  }

  return (
    <Root
      className={className}
      initial={{ opacity: 0, y: 28 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={viewport}
      transition={{ duration: 0.55, ease }}
    >
      {children}
    </Root>
  )
}

type ScrollRevealStaggerProps = {
  children: ReactNode
  className?: string
}

function ScrollRevealStagger({ children, className }: ScrollRevealStaggerProps) {
  const prefersReducedMotion = useReducedMotion()

  if (prefersReducedMotion) {
    return <div className={className}>{children}</div>
  }

  return (
    <StaggerRoot
      className={className}
      initial="hidden"
      whileInView="visible"
      viewport={viewport}
      variants={scrollRevealContainerVariants}
    >
      {children}
    </StaggerRoot>
  )
}

type ScrollRevealItemProps = {
  children: ReactNode
  className?: string
  style?: React.CSSProperties
  sequenceIndex?: number
}

function ScrollRevealItem({
  children,
  className,
  style,
  sequenceIndex,
}: ScrollRevealItemProps) {
  const prefersReducedMotion = useReducedMotion()

  if (prefersReducedMotion) {
    return (
      <div className={className} style={style}>
        {children}
      </div>
    )
  }

  if (sequenceIndex !== undefined) {
    return (
      <ItemRoot
        className={className}
        style={style}
        initial={{ opacity: 0, y: 28 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={viewport}
        transition={{
          duration: 0.5,
          delay: sequenceIndex * SCROLL_REVEAL_STAGGER_S,
          ease,
        }}
      >
        {children}
      </ItemRoot>
    )
  }

  return (
    <ItemRoot
      className={className}
      style={style}
      variants={scrollRevealItemVariants}
    >
      {children}
    </ItemRoot>
  )
}

export default ScrollReveal
export { ScrollRevealStagger, ScrollRevealItem }
